import {
  ARTICLE_TYPE,
  ContentItem,
  GIF_TYPE,
  VIDEO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { STOCK_GIF_TYPE, STOCK_VIDEO_TYPE } from 'interfaces/Content/StockContentTypes'
import { createSelector } from 'reselect'
import { connectedDestinationsSelector } from 'services/destinations'
import {
  BRAND_FACEBOOK,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  getFileExtensionFromUrl,
  PostDestination
} from 'shared'
import {
  ALT_TEXT_MAX_LENGTH,
  BRAND_BLUESKY,
  BRAND_THREADS,
  FB_POST_TYPE_REEL,
  HASHTAG_REGEXP,
  LINKEDIN_ALLOWED_IMAGE_EXTENSIONS,
  MAX_BLUESKY_STATUS_LENGTH,
  MAX_COMPOSER_PHOTOS_COUNT_BLUESKY,
  MAX_COMPOSER_PHOTOS_COUNT_LINKEDIN,
  MAX_COMPOSER_PHOTOS_COUNT_TWITTER,
  MAX_FACEBOOK_STATUS_LENGTH,
  MAX_FB_STORY_ASPECT_RATIO,
  MAX_GMB_COUPON_CODE_LENGTH,
  MAX_GMB_EVENT_TITLE_LENGTH,
  MAX_GMB_STATUS_LENGTH,
  MAX_INSTAGRAM_HASHTAGS,
  MAX_INSTAGRAM_STATUS_LENGTH,
  MAX_INSTAGRAM_USER_TAGS,
  MAX_LINKEDIN_STATUS_LENGTH,
  MAX_PINTEREST_HASHTAGS,
  MAX_THREADS_IMAGES,
  MAX_THREADS_STATUS_LENGTH,
  MAX_TIKTOK_VIDEO_DURATION_SEC,
  MAX_YT_TITLE_LENGTH,
  MIN_FB_STORY_DURATION,
  MIN_FB_STORY_HEIGHT,
  MIN_FB_STORY_WIDTH,
  NETWORKS_ORDERED,
  PHOTO_TYPE,
  PINTEREST_DESCRIPTION_MAX_LENGTH,
  PINTEREST_TITLE_MAX_LENGTH,
  POST_TYPE_STORY,
  STOCK_PHOTO_TYPE,
  TIKTOK_ALLOWED_IMAGE_EXTENSIONS,
  TIKTOK_ALLOWED_VIDEO_FILE_EXTENSIONS,
  TIKTOK_MAX_PHOTOS_COUNT,
  TIKTOK_PHOTO_DESCRIPTION_MAX_LENGTH,
  TIKTOK_PHOTO_TITLE_MAX_LENGTH,
  urlRegex
} from 'shared/constants'
import {
  ComposerBlueskyPost,
  ComposerFacebookPost,
  ComposerGooglePost,
  ComposerInstagramPost,
  ComposerLinkedinPost,
  ComposerPinterestPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  GOOGLE_POST_TYPE_EVENT,
  GOOGLE_POST_TYPE_OFFER,
  GOOGLE_POST_TYPE_UPDATE,
  LinkDetails,
  PostDestinationType
} from 'shared/types'
import StoreState from 'store/state'
import { extractUrlsFromText, getTwitterRemainingCharsPremium, getTwitterRemainingCharsRegular } from 'utils/composer'
import { sortByKeyAscending } from 'utils/sort/order'
import ComposerState from './state'

export const composerSelector = (state: StoreState) => state.composer
// EXPL: keyed off `composer.selectedProfiles` instead of the whole composer slice. Taking
// `composerSelector` as input recomputed this on every composer action and handed every
// downstream selector a brand new array, so nothing below it could stay memoized.
const selectedProfileIdsSelector = (state: StoreState) => state.composer.selectedProfiles
export const composerSelectedProfilesSelector = createSelector(
  selectedProfileIdsSelector,
  connectedDestinationsSelector,
  (selectedProfiles: string[], profiles: Record<string, PostDestination>) => {
    return selectedProfiles.map(id => profiles[id]).filter(Boolean) as PostDestination[]
  }
)
export const composerSelectedTikTokProfilesSelector = createSelector(
  composerSelectedProfilesSelector,
  profiles => profiles.filter(p => p.type === BRAND_TIKTOK)
)
export const composerSelectedProfilesIdsSelector = createSelector(composerSelectedProfilesSelector, profiles => profiles.map(p => p.id))
export const composerSelectedProfilesCountSelector = createSelector(composerSelectedProfilesSelector, profiles => profiles.length)
export const composerSelectedProfilePpidsSelector = createSelector(composerSelectedProfilesSelector, profiles => profiles.map(p => p.ppid))
export const composerTabsSelector = createSelector(composerSelectedProfilesSelector, profiles => {
  const networks = profiles.reduce((tabs: Record<string, boolean>, profile: PostDestination) => {
    tabs[profile.type] = true
    return tabs
  }, {})
  return Object.keys(networks) as PostDestinationType[]
})

export const composerActiveTabSelector = createSelector(
  composerSelector,
  composerTabsSelector,
  (state: ComposerState, tabs: PostDestinationType[]) => {
    if (tabs.includes(state.activeTab)) {
      return state.activeTab
    }
    return tabs[0] || state.activeTab
  }
)
export const composerNetworksViewSelector = createSelector(composerSelector, (state: ComposerState) => state.networksView)
export const composerContentItemSelector = createSelector(composerSelector, (state: ComposerState) => state.contentItem)
export const composerBulkFileSelector = createSelector(composerSelector, (state: ComposerState) => state.bulkUploadFile)
export const composerBulkFileUrlSelector = createSelector(composerBulkFileSelector, file => file?.url)
export const composerRecycleSelector = createSelector(composerSelector, (state: ComposerState) => state.recycle)
export const composerLinkSelector = createSelector(composerSelector, (state: ComposerState) => state.links.find(link => link.selected))
export const composerAllLinksSelector = createSelector(composerSelector, (state: ComposerState) => state.links)
export const composerPostProgressSelector = createSelector(composerSelector, (state: ComposerState) => state.progress)
export const composerNetworkPostsSelector = createSelector(composerSelector, (state: ComposerState) => state.posts)
export const rememberIGLocationsSelector = createSelector(composerSelector, state => state.rememberIGLocations)
export const composerInstagramCoverUrlSelector = createSelector(composerSelector, state => state.posts.instagram.coverUrl)
export const pinterestProfilesBoardsSelector = createSelector(composerSelector, state => state.pinterestBoards)
export const composerSelectedBucketIdSelector = createSelector(composerSelector, state => state.selectedBucketId)
export const composerPostAsPhotoSelector = createSelector(composerSelector, (state: ComposerState) => state.postAsPhoto)
export const composerMultiPostSelector = createSelector(composerSelector, (state: ComposerState) => state.multiPost)

export const composerVideoSelector = createSelector(composerSelector, (state: ComposerState) => state.video)
export const composerImagesSelector = createSelector(composerSelector, (state: ComposerState) => state.images)
export const composerImageUrlsSelector = createSelector(composerImagesSelector, (images) => {
  return Object.values(images).map(item => item.url).filter(Boolean) as string[]
})

// EXPL: The post carries an image either as an upload or as a photo/gif content item.
export const composerWithImagesSelector = createSelector(
  composerImageUrlsSelector,
  composerContentItemSelector,
  (imageUrls, content) => imageUrls.length > 0
    || content?.type === PHOTO_TYPE
    || content?.type === STOCK_PHOTO_TYPE
    || content?.type === GIF_TYPE
    || content?.type === STOCK_GIF_TYPE
)

export const composerAltTextEnabledSelector = createSelector(composerSelector, (state: ComposerState) => Boolean(state.altTextEnabled))
export const composerAltTextSelector = createSelector(composerSelector, (state: ComposerState) => state.altText || '')
// NOTE: gated on images - the control only renders with images attached, so without this an
// over-length value left behind by a removed image would disable every submit path invisibly.
export const composerAltTextErrorSelector = createSelector(
  composerAltTextEnabledSelector,
  composerAltTextSelector,
  composerWithImagesSelector,
  (enabled, text, withImages) => enabled && withImages && text.length > ALT_TEXT_MAX_LENGTH
)
export const featuredImageUrlSelector = createSelector(composerImagesSelector, images => {
  return Object.values(images).find(item => item.order === 1)?.url
})
export const composerVideoUrlSelector = createSelector(composerVideoSelector, (video) => {
  return video?.url
})
export const composerVideoDurationSelector = createSelector(composerVideoSelector, (video) => video?.duration)
export const composerVideoSizeSelector = createSelector(composerVideoSelector, (video) => video?.size)
export const composerVideoThumbnailSelector = createSelector(composerVideoSelector, (video) => video?.thumbnailUrl)
export const composerVideoThumbnailOffsetSelector = createSelector(composerVideoSelector, (video) => video?.thumbnailOffset)
export const videoMetadataSelector = createSelector(composerVideoSelector, (video) => video?.metadata)
export const composerBulkPostsSelector = createSelector(composerMultiPostSelector, (state) => {
  return Object.values(state.posts).sort(sortByKeyAscending('order'))
})
export const multiPostsArraySelector = createSelector(composerMultiPostSelector, state => Object.values(state.posts))
export const multiPostsUploadingSelector = createSelector(multiPostsArraySelector, posts => {
  return posts.some(p => p.imageUpload && !p.imageUpload.error && !p.imageUpload.url)
})
export const visibleMultiPostsSelector = createSelector(composerBulkPostsSelector, composerTabsSelector, (posts, networks) => {
  return posts.filter(p => networks.includes(p.network))
})
export const multiPostsCountSelector = createSelector(visibleMultiPostsSelector, composerSelectedProfilesSelector, (posts, profiles) => {
  const postsByNetwork = posts.reduce((count: Record<string, number>, post) => {
    if (count[post.network]) {
      count[post.network] += 1
    } else {
      count[post.network] = 1
    }
    return count
  }, {})

  return profiles.reduce((totalCount: number, profile) => {
    const count = typeof postsByNetwork[profile.type] === 'undefined' ? 0 : postsByNetwork[profile.type]
    return totalCount + count
  }, 0)
})
export const useGenericStatusSelector = createSelector(composerSelector, (state: ComposerState) => state.useGenericStatus)
export const composerPostSelector = createSelector(
  [composerNetworkPostsSelector, (_s, network: PostDestinationType) => network],
  (posts, network) => posts[network]
)
export const facebookPostSelector = createSelector(composerSelector, state => state.posts.facebook)
export const instagramPostSelector = createSelector(composerSelector, state => state.posts.instagram)
export const pinterestPostSelector = createSelector(composerSelector, state => state.posts.pinterest)
export const linkedinPostSelector = createSelector(composerSelector, state => state.posts.linkedin)
export const googlePostSelector = createSelector(composerSelector, state => state.posts.google)
export const youtubePostSelector = createSelector(composerSelector, state => state.posts.youtube)
export const threadsPostSelector = createSelector(composerSelector, state => state.posts.threads)
export const twitterPostSelector = createSelector(composerSelector, state => state.posts.twitter)
export const blueskyPostSelector = createSelector(composerSelector, state => state.posts.bluesky)
export const tiktokPostSelector = createSelector(composerSelector, state => state.posts.tiktok)
export const genericPostSelector = createSelector(composerSelector, state => state.posts.generic)
export const activeDraftSelector = createSelector(composerSelector, state => state.draft)
export const activePostIdSelector = createSelector(composerSelector, state => state.postEdit?.id)
export const composerPostEditSelector = createSelector(composerSelector, state => state.postEdit)
export const composerScheduleSelector = createSelector(composerSelector, state => state.schedule)
export const composerPromptSelector = createSelector(composerSelector, state => state.prompt)
export const composerDocumentUploadSelector = createSelector(composerSelector, state => state.documentUpload)

export const aiWriterSelector = createSelector(composerSelector, state => state.writer)
export const favoritePromptsSelector = createSelector(aiWriterSelector, writer => writer.favoritePrompts)
export const userAIPromptsSelector = createSelector(aiWriterSelector, writer => {
  return [...writer.userPrompts].sort((a, b) => {
    const createdA = new Date(a.createdAt).getTime()
    const createdB = new Date(b.createdAt).getTime()
    return createdB - createdA
  })
})
export const aiWriterPromptIdSelector = createSelector(aiWriterSelector, writer => writer.aiWriterPromptId)

export const composerStatusesTextSelector = createSelector(composerSelector, (state: ComposerState) => {
  return NETWORKS_ORDERED.reduce((map: Record<string, string>, network: string) => {
    map[network] = (state.posts as any)[network].status.text
    return map
  }, {})
})

export const composerIsUploadingSelector = createSelector(composerImagesSelector, composerVideoSelector, (images, video) => {
  if (video?.isUploading) {
    return 'video'
  }
  return Object.values(images).some(item => item.isUploading)
})

const composerMediaIsEmptySelector = createSelector(
  composerImageUrlsSelector,
  composerVideoUrlSelector,
  composerContentItemSelector,
  composerLinkSelector,
  composerDocumentUploadSelector,
  (imageUrls, videoUrl, contentItem, link, doc) => {
    return imageUrls.length === 0 && !videoUrl && !link && (!contentItem || contentItem.type === TEXT_TYPE) && !doc
  }
)

/** The composer-level AI content flag - `getPostData` writes it into every network that carries it. */
export const composerIsAiContentSelector = (state: StoreState) => Boolean(state.composer.aiContent)
/** The composer-level "AI content" checkbox: shown whenever media is attached - the flag is sent where a network supports it. */
export const composerAiContentSelector = createSelector(
  composerIsAiContentSelector,
  composerMediaIsEmptySelector,
  (checked, isMediaEmpty) => ({
    visible: !isMediaEmpty,
    checked
  })
)
/** Facebook ignores the AI content flag on photo posts - true when the composer would send one to a Facebook profile. */
export const composerAiContentFacebookPhotoSelector = createSelector(
  composerTabsSelector,
  composerWithImagesSelector,
  composerVideoUrlSelector,
  (networks, withImages, videoUrl) => networks.includes(BRAND_FACEBOOK) && withImages && !videoUrl
)

// Used to check if media is added to document post
const composerMediaIsEmptyLinkedinSelector = createSelector(
  composerImageUrlsSelector,
  composerVideoUrlSelector,
  composerContentItemSelector,
  composerLinkSelector,
  (imageUrls, videoUrl, contentItem, link) => {
    return imageUrls.length === 0 && !videoUrl && !link && (!contentItem || contentItem.type === TEXT_TYPE)
  }
)

/**
 * ================================================
 * Posts errors and hints selectors
 * ================================================
 */
export interface ComposerPostStatus {
  network: {
    hints: string[]
    errors: string[]
    info: string[]
  },
  caption: {
    hints: string[]
    errors: string[]
    info: string[]
  },
  content: {
    hints: string[]
    errors: string[]
    info: string[]
  },
  hasErrors: boolean
  hasHints: boolean
  isEmpty?: boolean
}
function emptyPostStatus(): ComposerPostStatus {
  return {
    network: {
      hints: [],
      errors: [],
      info: []
    },
    caption: {
      hints: [],
      errors: [],
      info: []
    },
    content: {
      hints: [],
      errors: [],
      info: []
    },
    hasErrors: false,
    hasHints: false
  }
}

export const composerInstagramPostStatusSelector = createSelector(
  instagramPostSelector,
  composerImageUrlsSelector,
  composerSelectedProfilesSelector,
  composerPostAsPhotoSelector,
  composerDocumentUploadSelector,
  composerVideoUrlSelector,
  composerBulkFileUrlSelector,
  composerContentItemSelector,
  composerLinkSelector,
  composerMediaIsEmptySelector,
  (
    post: ComposerInstagramPost,
    imageUrls: string[],
    profiles: PostDestination[],
    postAsPhoto: boolean,
    document,
    videoUrl?: string,
    bulkUploadFileUrl?: string,
    content?: ContentItem,
    link?: LinkDetails,
    mediaIsEmpty?: boolean
  ) => {
    const postState = emptyPostStatus()
    const igProfiles = profiles.filter(p => p.type === BRAND_INSTAGRAM)
    if (igProfiles.length === 0) {
      return postState
    }

    const hasIGDirect = igProfiles.some(p => Boolean(p.instagramDirect))
    const hasNonIGDirect = igProfiles.some(p => !p.instagramDirect)
    if (hasIGDirect && hasNonIGDirect) {
      postState.network.hints.push('composer.hints.instagram.mixed-direct-connect')
      postState.hasHints = true
    }

    if (document) {
      postState.content.errors.push('composer.errors.instagram.document-post')
      postState.hasErrors = true
    }

    if (post.postType === POST_TYPE_STORY && !mediaIsEmpty) {
      postState.content.info.push('composer.info.instagram.story-ratio')
    }

    const commentHashtags: string[] = post.comments.flatMap(c => c.text.match(HASHTAG_REGEXP) || [])
    const postHashtags: string[] = (post.status.text.match(HASHTAG_REGEXP) || [])
    const hasDuplicatedHashtags = commentHashtags.some(tag => postHashtags.includes(tag))

    if (hasDuplicatedHashtags) {
      postState.caption.hints.push('composer.hints.instagram.hashtags-duplicated')
      postState.hasHints = true
    }

    if (commentHashtags.length > MAX_INSTAGRAM_HASHTAGS) {
      postState.caption.errors.push('composer.errors.instagram.hashtags-limit-comment')
      postState.hasErrors = true
    }

    if (post.status.text.length > MAX_INSTAGRAM_STATUS_LENGTH) {
      postState.caption.errors.push('composer.errors.instagram.caption-length')
      postState.hasErrors = true
    }

    if (extractUrlsFromText(post.status.text).length > 0) {
      postState.caption.hints.push('composer.hints.instagram.url-in-caption')
      postState.hasHints = true
    }

    if (
      post.postType !== POST_TYPE_STORY && post.status.text.indexOf('#') === -1
      && post.comments.every(c => c.text.indexOf('#') === -1)
    ) {
      postState.caption.hints.push('composer.hints.instagram.no-hashtags')
      postState.hasHints = true
    } else if (postHashtags.length > MAX_INSTAGRAM_HASHTAGS) {
      postState.caption.errors.push('composer.errors.instagram.hashtags-limit')
      postState.hasErrors = true
    }
    // TODO: Vid/Image dimensions check
    // if (videoUrl) {
    //   postState.content.hints.push('composer.labels.instagram-vid-requirements')
    //   postState.content.hints.push('composer.labels.instagram-vid-requirements-hint')
    //   postState.hasHints = true
    // }

    const mediaEmpty = imageUrls.length === 0 && !videoUrl

    for (const image of imageUrls) {
      const fileExtension = getFileExtensionFromUrl(image)
      if (fileExtension === 'png') {
        postState.caption.hints.push('composer.hints.instagram.photo-formats')
        postState.hasHints = true
        break
      }
    }

    if (post.status.text.length === 0 && mediaEmpty && !bulkUploadFileUrl && !content && !link && !postAsPhoto) {
      postState.isEmpty = true
      postState.content.errors.push('composer.errors.instagram.post-empty')
      postState.hasErrors = true
    }

    if (post.postType === POST_TYPE_STORY) {
      if (imageUrls.length > 1) {
        postState.network.hints.push('composer.hints.instagram.multi-photo-stories')
        postState.hasHints = true
      }
      if (post.status.text.length > 0) {
        postState.network.hints.push('composer.hints.instagram.caption-stories')
        postState.hasHints = true
      }
      if (post.comments.some(c => c.text.length > 0)) {
        postState.network.hints.push('composer.hints.instagram.story-first-comment')
        postState.hasHints = true
      }
    }

    if (post.tags && post.tags.length > MAX_INSTAGRAM_USER_TAGS) {
      postState.network.hints.push('composer.hints.instagram.user-tags-limit')
      postState.hasHints = true
    }

    const withLocation = igProfiles.some(p => post.locations[p.ppid])
    if (withLocation && (videoUrl || imageUrls.length > 1)) {
      postState.network.hints.push('composer.hints.instagram.post-with-location')
      postState.hasHints = true
    }

    if (mediaEmpty && !postState.isEmpty && !postAsPhoto) {
      postState.content.errors.push('composer.errors.instagram.content-empty')
      postState.hasErrors = true
    }

    return postState
  }
)

export const composerPinterestPostStatusSelector = createSelector(
  pinterestPostSelector,
  composerMediaIsEmptySelector,
  composerSelectedProfilesSelector,
  composerImageUrlsSelector,
  pinterestProfilesBoardsSelector,
  composerDocumentUploadSelector,
  composerContentItemSelector,
  composerRecycleSelector,
  composerScheduleSelector,
  (
    post: ComposerPinterestPost,
    mediaIsEmpty: boolean,
    profiles: PostDestination[],
    imageUrls: string[],
    boards: { [ppid: string]: Array<{ id: string, name: string, url?: string, selected?: boolean }> },
    document,
    content: ContentItem | undefined,
    recycle,
    schedule
  ) => {
    const postState = emptyPostStatus()
    const pinterestProfiles = profiles.filter(p => p.type === BRAND_PINTEREST)
    if (pinterestProfiles.length === 0) {
      return postState
    }

    if (pinterestProfiles.length > 1) {
      postState.network.errors.push('composer.errors.pinterest.multi-account')
      postState.hasErrors = true
    }

    if (recycle || schedule?.repeat) {
      postState.network.errors.push('composer.errors.pinterest.recycle')
      postState.hasErrors = true
    }

    if (document) {
      postState.content.errors.push('composer.errors.pinterest.document-post')
      postState.hasErrors = true
    }

    for (const profile of pinterestProfiles) {
      if (!boards[profile.ppid] || !boards[profile.ppid].find(b => b.selected)) {
        postState.caption.errors.push(PINTEREST_BOARD_NOT_SELECTED_MESSAGE_KEY)
        postState.hasErrors = true
        break
      }
    }

    const { title, description: d, destinationUrl } = post
    const description = d || ''

    if (destinationUrl && !destinationUrl.match(urlRegex)) {
      postState.caption.errors.push('composer.errors.pinterest.link-invalid-url')
      postState.hasErrors = true
    }

    if (title.length > PINTEREST_TITLE_MAX_LENGTH) {
      postState.caption.errors.push('composer.errors.pinterest.title-limit-reached')
      postState.hasErrors = true
    }

    if (description?.length > PINTEREST_DESCRIPTION_MAX_LENGTH) {
      postState.caption.errors.push('composer.errors.pinterest.description-limit-reached')
      postState.hasErrors = true
    }

    if (description?.indexOf('\n') !== -1) {
      postState.caption.info.push('composer.info.pinterest.newline')
    }

    if ((description?.match(/#/g) || []).length > MAX_PINTEREST_HASHTAGS) {
      postState.content.hints.push('composer.hints.pinterest.hashtag-limit')
      postState.hasHints = true
    }

    // if (title.trim().length + description.trim().length < MIN_PINTEREST_TITLE_PLUS_DESCRIPTION_LENGTH) {
    //   postState.caption.errors.push('composer.errors.pinterest.title-description-length')
    //   postState.hasErrors = true
    // }

    if (content?.type === VIDEO_TYPE) {
      postState.content.errors.push('composer.errors.pinterest.no-video-links')
      postState.hasErrors = true
    }

    if (imageUrls.length > 1) {
      postState.content.hints.push('composer.hints.pinterest.multi-photo')
      postState.hasHints = true
    }

    if (mediaIsEmpty) {
      if (!title && !description?.trim() && !destinationUrl) {
        postState.isEmpty = true
        postState.content.errors.push('composer.errors.pinterest.post-empty')
        postState.hasErrors = true
      } else {
        postState.content.errors.push('composer.errors.pinterest.content-empty')
        postState.hasErrors = true
      }
    }

    // TODO: add image orientation check: "Optimized Pin images have a square or profile aspect ratio"

    return postState
  }
)

export const composerLinkedinPostStatusSelector = createSelector(
  composerImageUrlsSelector,
  linkedinPostSelector,
  composerMediaIsEmptyLinkedinSelector,
  composerSelectedProfilesSelector,
  composerDocumentUploadSelector,
  (imageUrls: string[], post: ComposerLinkedinPost, mediaIsEmpty: boolean, profiles: PostDestination[], document) => {
    const postState = emptyPostStatus()
    postState.isEmpty = false
    const liProfiles = profiles.filter(p => p.type === BRAND_LINKEDIN)
    if (liProfiles.length === 0) {
      return postState
    }

    if (imageUrls.length > MAX_COMPOSER_PHOTOS_COUNT_LINKEDIN) {
      postState.caption.errors.push('composer.errors.linkedin.max-photos')
      postState.hasErrors = true
    }

    for (const image of imageUrls) {
      const fileExtension = getFileExtensionFromUrl(image)
      if (fileExtension && !LINKEDIN_ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
        postState.caption.errors.push('composer.errors.linkedin.photo-formats')
        postState.hasErrors = true
        break
      }
    }

    const status = post.status.text.trim()

    if (status.length > MAX_LINKEDIN_STATUS_LENGTH) {
      postState.caption.errors.push('composer.errors.linkedin.caption-length')
      postState.hasErrors = true
    } else if (status.length === 0 && mediaIsEmpty && !document) {
      postState.isEmpty = true
      postState.content.errors.push('composer.errors.linkedin.post-empty')
      postState.hasErrors = true
    } else if (status.length === 0 && document) {
      postState.isEmpty = false
      postState.content.errors.push('composer.errors.linkedin.doc-no-caption')
      postState.hasErrors = true
    }

    if (!mediaIsEmpty && document) {
      postState.content.errors.push('composer.errors.linkedin.doc-and-media')
      postState.hasErrors = true
    }

    return postState
  }
)

export const composerFacebookPostStatusSelector = createSelector(
  facebookPostSelector,
  composerMediaIsEmptySelector,
  composerSelectedProfilesSelector,
  composerImageUrlsSelector,
  composerDocumentUploadSelector,
  composerVideoUrlSelector,
  composerVideoDurationSelector,
  videoMetadataSelector,
  (
    post: ComposerFacebookPost,
    mediaIsEmpty: boolean,
    profiles: PostDestination[],
    images: string[],
    document,
    videoUrl?: string,
    videoDuration?: number,
    videoMetadata?: { width?: number, height?: number, fileType?: string }
  ) => {
    const postState = emptyPostStatus()
    const isFBGroupSelected = Boolean(profiles.find(p => p.fbGroup))
    if (profiles.find(p => p.type === BRAND_FACEBOOK) === undefined) {
      return postState
    }

    if (document) {
      postState.content.errors.push('composer.errors.facebook.document-post')
      postState.hasErrors = true
    }

    if (post.postType === FB_POST_TYPE_REEL && isFBGroupSelected) {
      postState.content.errors.push('composer.errors.facebook.reel-to-group')
      postState.hasErrors = true
    }

    if (post.postType === POST_TYPE_STORY) {
      if (isFBGroupSelected) {
        postState.content.errors.push('composer.errors.facebook.story-video-to-group')
        postState.hasErrors = true
      }
      if (post.status.text) {
        postState.content.hints.push('composer.hints.facebook.story-caption')
        postState.hasHints = true
      }
    }

    if (videoUrl && [POST_TYPE_STORY, FB_POST_TYPE_REEL].includes(post.postType as any)) {
      const fileType = videoMetadata?.fileType || videoUrl.substring(videoUrl.lastIndexOf('.') + 1).toLowerCase()
      if (post.comments.some(c => c.text.trim())) {
        postState.content.hints.push('composer.hints.facebook.story-comment')
        postState.hasHints = true
      }
      if (videoMetadata?.width && videoMetadata?.height) {
        if (videoMetadata.width / videoMetadata.height > MAX_FB_STORY_ASPECT_RATIO) {
          postState.content.errors.push('composer.errors.facebook.story-aspect-ratio')
          postState.hasErrors = true
        }
        if (videoMetadata.width < MIN_FB_STORY_WIDTH || videoMetadata.height < MIN_FB_STORY_HEIGHT) {
          postState.content.errors.push('composer.errors.facebook.story-width-height')
          postState.hasErrors = true
        }
      }
      if (
        post.postType === POST_TYPE_STORY
        && videoDuration
        && (videoDuration < MIN_FB_STORY_DURATION || videoDuration > MIN_FB_STORY_DURATION)
      ) {
        postState.content.errors.push('composer.hints.facebook.story-duration')
        postState.hasHints = true
      }
      if (fileType && fileType !== 'mp4') {
        postState.content.hints.push('composer.hints.facebook.story-file-type')
        postState.hasHints = true
      }
    }

    if (post.status.text.trim().length === 0) {
      if (mediaIsEmpty) {
        postState.isEmpty = true
        postState.content.errors.push('composer.errors.facebook.post-empty')
        postState.hasErrors = true
      }
    } else if (post.status.text.length > MAX_FACEBOOK_STATUS_LENGTH) {
      postState.caption.errors.push('composer.errors.facebook.caption-length')
      postState.hasErrors = true
    }

    if (images.length > 1 && profiles.find(p => p.fbGroup && p.ppPublishBy) !== undefined) {
      postState.content.errors.push('composer.errors.facebook.multi-photo-to-group')
      postState.hasErrors = true
    }

    if (isFBGroupSelected && post.status.html.indexOf('data-tag-id') !== -1) {
      postState.caption.hints.push('composer.hints.facebook.tags-to-groups')
      postState.hasHints = true
    }

    if (post.comments.some(c => c.text.trim())) {
      postState.caption.info.push('composer.hints.facebook.comment-info')
      if (isFBGroupSelected) {
        postState.caption.hints.push('composer.hints.facebook.comments-group')
        postState.hasHints = true
      }

      const commentHashtags: string[] = post.comments.flatMap(c => c.text.match(HASHTAG_REGEXP) || [])
      const postHashtags: string[] = (post.status.text.match(HASHTAG_REGEXP) || [])
      const hasDuplicatedHashtags = commentHashtags.some(tag => postHashtags.includes(tag))
      if (hasDuplicatedHashtags) {
        postState.caption.hints.push('composer.hints.facebook.hashtags-duplicated')
        postState.hasHints = true
      }
    }

    return postState
  }
)

export const composerTwitterPostStatusSelector = createSelector(
  twitterPostSelector,
  composerSelectedProfilesSelector,
  composerMediaIsEmptySelector,
  composerImageUrlsSelector,
  composerDocumentUploadSelector,
  (
    post: ComposerTwitterPost,
    profiles: PostDestination[],
    mediaIsEmpty: boolean,
    imageUrls: string[],
    document
  ) => {
    const postState = emptyPostStatus()
    if (profiles.find(p => p.type === BRAND_TWITTER) === undefined) {
      return postState
    }

    if (document) {
      postState.content.errors.push('composer.errors.twitter.document-post')
      postState.hasErrors = true
    }

    const status = post.status.text
    // const isUrlRequired = Boolean(link) || content?.type === ARTICLE_TYPE || content?.type === VIDEO_TYPE

    // if (isUrlRequired && post.tweetType === 'new' && !postAsPhoto && extractUrlsFromText(status).length === 0) {
    //   postState.caption.hints.push('composer.hints.twitter.no-article-url')
    //   postState.hasHints = true
    // }

    if (extractUrlsFromText(status).length > 0) {
      postState.content.errors.push('composer.errors.twitter.article-post')
      postState.hasErrors = true
    }

    if (getTwitterRemainingCharsRegular(status) < 0) {
      postState.caption.hints.push('composer.hints.twitter.tweet-length-premium')
      postState.hasHints = true
    }

    if (getTwitterRemainingCharsPremium(status) < 0) {
      postState.caption.errors.push('composer.errors.twitter.tweet-length')
      postState.hasErrors = true
    } else if (status.length === 0) {
      if (mediaIsEmpty) {
        postState.content.errors.push('composer.errors.twitter.post-empty')
        postState.hasErrors = true
        postState.isEmpty = true
      }
    }

    if (imageUrls.length > MAX_COMPOSER_PHOTOS_COUNT_TWITTER) {
      postState.content.errors.push('composer.errors.twitter.max-photos')
      postState.hasErrors = true
    }

    return postState
  }
)

export const composerBlueskyPostStatusSelector = createSelector(
  blueskyPostSelector,
  composerSelectedProfilesSelector,
  composerMediaIsEmptySelector,
  composerDocumentUploadSelector,
  composerImageUrlsSelector,
  composerContentItemSelector,
  composerLinkSelector,
  composerPostAsPhotoSelector,
  (
    post: ComposerBlueskyPost,
    profiles: PostDestination[],
    mediaIsEmpty: boolean,
    document,
    imageUrls: string[],
    content?: ContentItem,
    link?: LinkDetails,
    postAsPhoto?: boolean
  ) => {
    const postState = emptyPostStatus()
    if (profiles.find(p => p.type === BRAND_BLUESKY) === undefined) {
      return postState
    }

    if (document) {
      postState.content.errors.push('composer.errors.bluesky.document-post')
      postState.hasErrors = true
    }

    const status = post.status.text.trim()
    const isUrlRequired = Boolean(link) || content?.type === ARTICLE_TYPE || content?.type === VIDEO_TYPE

    if (isUrlRequired && !postAsPhoto && extractUrlsFromText(status).length === 0) {
      postState.caption.hints.push('composer.hints.bluesky.no-article-url')
      postState.hasHints = true
    }

    if (post.comments.some(c => c.text.trim().length > MAX_BLUESKY_STATUS_LENGTH)) {
      postState.caption.errors.push('composer.errors.bluesky.comment-length')
      postState.hasErrors = true
    }

    if (status.length === 0) {
      if (mediaIsEmpty) {
        postState.content.errors.push('composer.errors.bluesky.post-empty')
        postState.hasErrors = true
        postState.isEmpty = true
      }
    }

    if (status.length > MAX_BLUESKY_STATUS_LENGTH) {
      postState.caption.errors.push('composer.errors.bluesky.caption-length')
      postState.hasErrors = true
    }

    if (imageUrls.length > MAX_COMPOSER_PHOTOS_COUNT_BLUESKY) {
      postState.content.errors.push('composer.errors.bluesky.max-photos')
      postState.hasErrors = true
    }

    return postState
  }
)

export const composerTiktokPostStatusSelector = createSelector(
  tiktokPostSelector,
  composerVideoUrlSelector,
  composerVideoDurationSelector,
  composerSelectedProfilesSelector,
  composerMediaIsEmptySelector,
  composerDocumentUploadSelector,
  composerImageUrlsSelector,
  composerPostAsPhotoSelector,
  (post, url, duration, profiles, isMediaEmpty, document, imageUrls, postAsPhoto) => {
    const postState = emptyPostStatus()
    const tiktokProfiles = profiles.filter((p: PostDestination) => p.type === BRAND_TIKTOK)
    if (tiktokProfiles.length === 0) {
      return postState
    }

    if (document) {
      postState.content.errors.push('composer.errors.tiktok.document-post')
      postState.hasErrors = true
    }

    if (isMediaEmpty && post.status.text.trim().length === 0) {
      postState.isEmpty = true
    }
    const settings = post.settings || {}
    for (const profile of tiktokProfiles) {
      if (!settings[profile.ppid]?.privacyLevel && !postState.content.errors.includes('composer.errors.tiktok.privacy')) {
        postState.content.errors.push('composer.errors.tiktok.privacy')
        postState.hasErrors = true
      }
      if (!settings[profile.ppid]?.promoting && !postState.content.errors.includes('composer.errors.tiktok.promotion')) {
        postState.content.errors.push('composer.errors.tiktok.promotion')
        postState.hasErrors = true
      }
    }

    if (url) {
      const fileExtension = getFileExtensionFromUrl(url)
      if (!TIKTOK_ALLOWED_VIDEO_FILE_EXTENSIONS.includes(fileExtension as any)) {
        postState.content.errors.push('composer.errors.tiktok.video-type')
        postState.hasErrors = true
      }
      if (duration && duration > MAX_TIKTOK_VIDEO_DURATION_SEC) {
        postState.content.errors.push('composer.errors.tiktok.video-duration')
        postState.hasErrors = true
      }
      if (postState.content.errors.length === 0) {
        postState.content.info = [
          'composer.hints.tiktok.video-resolution',
          'composer.hints.tiktok.video-size'
        ]
      }
    }

    if (!url && imageUrls.length === 0 && !postAsPhoto) {
      postState.content.errors.push('composer.errors.tiktok.no-media')
      postState.hasErrors = true
    }

    if (imageUrls.length > TIKTOK_MAX_PHOTOS_COUNT) {
      postState.content.errors.push('composer.errors.tiktok.max-photos')
      postState.hasErrors = true
    }

    for (const image of imageUrls) {
      const fileExtension = getFileExtensionFromUrl(image)
      if (fileExtension && !TIKTOK_ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
        postState.caption.errors.push('composer.errors.tiktok.photo-formats')
        postState.hasErrors = true
        break
      }
    }

    if (imageUrls.length > 0) {
      const { title, description } = post
      if (title.length > TIKTOK_PHOTO_TITLE_MAX_LENGTH) {
        postState.caption.errors.push('composer.errors.tiktok.photo-title-limit')
        postState.hasErrors = true
      }
      if (description.length > TIKTOK_PHOTO_DESCRIPTION_MAX_LENGTH) {
        postState.caption.errors.push('composer.errors.tiktok.photo-description-limit')
        postState.hasErrors = true
      }
    }

    return postState
  }
)

export const composerGooglePostStatusSelector = createSelector(
  googlePostSelector,
  composerMediaIsEmptySelector,
  composerSelectedProfilesSelector,
  composerImageUrlsSelector,
  composerContentItemSelector,
  composerDocumentUploadSelector,
  composerVideoUrlSelector,
  composerContentItemSelector,
  composerLinkSelector,
  (
    post: ComposerGooglePost,
    mediaIsEmpty: boolean,
    profiles: PostDestination[],
    imageUrls: string[],
    contentItem: ContentItem | undefined,
    document,
    videoUrl?: string,
    content?: ContentItem,
    linkDetails?: LinkDetails
  ) => {
    const postState = emptyPostStatus()
    if (profiles.find(p => p.type === BRAND_GOOGLE) === undefined) {
      return postState
    }

    if (document) {
      postState.content.errors.push('composer.errors.google.document-post')
      postState.hasErrors = true
    }

    const status = post.status.text.trim()
    const link = (post as ComposerGooglePost).update.link?.trim()
    const withVideoLink = content?.type === VIDEO_TYPE || linkDetails?.type === 'video_link'

    let withGifImage = false
    for (let index = 0; index < imageUrls.length; index++) {
      const fileExtension = getFileExtensionFromUrl(imageUrls[index])
      if (fileExtension === 'gif') {
        withGifImage = true
        break
      }
    }

    if (status.length === 0) {
      if (mediaIsEmpty) {
        postState.isEmpty = true
        postState.content.errors.push('composer.errors.google.post-empty')
        postState.hasErrors = true
      }
    } else if (status.length > MAX_GMB_STATUS_LENGTH) {
      postState.content.errors.push('composer.errors.google.post-length')
      postState.hasErrors = true
    }

    if (link && !link.match(urlRegex)) {
      postState.content.hints.push('composer.hints.google.link-invalid-url')
      postState.hasHints = true
    }

    if (post.selectedPostType === GOOGLE_POST_TYPE_UPDATE && (!link || !(post as ComposerGooglePost).update.buttonType)) {
      postState.caption.hints.push('composer.hints.google.missing-link-or-btn-type')
      postState.hasHints = true
    }

    if (post.selectedPostType === GOOGLE_POST_TYPE_EVENT) {
      const eventTitle = post.event.title.trim()
      if (eventTitle.length === 0) {
        postState.content.errors.push('composer.errors.google.event-title-empty')
        postState.hasErrors = true
      } else if (eventTitle.length > MAX_GMB_EVENT_TITLE_LENGTH) {
        postState.content.errors.push('composer.errors.google.event-title-length')
        postState.hasErrors = true
      }
    }

    if (post.selectedPostType === GOOGLE_POST_TYPE_OFFER) {
      const code = post.offer.offer.couponCode ? post.offer.offer.couponCode.trim() : ''
      if (code.length > MAX_GMB_COUPON_CODE_LENGTH) {
        postState.content.errors.push('composer.errors.google.offer-code-length')
        postState.hasErrors = true
      }
    }

    if (post.selectedPostType === GOOGLE_POST_TYPE_OFFER) {
      const offerTitle = post.offer.event.title.trim()
      if (offerTitle.length === 0) {
        postState.content.errors.push('composer.errors.google.offer-title-empty')
        postState.hasErrors = true
      } else if (offerTitle.length > MAX_GMB_EVENT_TITLE_LENGTH) {
        postState.content.errors.push('composer.errors.google.offer-title-length')
        postState.hasErrors = true
      }
    }

    if (imageUrls.length > 1) {
      postState.content.hints.push('composer.hints.google.multi-post')
      postState.hasHints = true
    }

    if (videoUrl || contentItem?.type === VIDEO_TYPE || contentItem?.type === STOCK_VIDEO_TYPE || withVideoLink) {
      postState.content.errors.push('composer.errors.google.videos')
      postState.hasErrors = true
    }

    if (withGifImage || contentItem?.type === GIF_TYPE || contentItem?.type === STOCK_GIF_TYPE) {
      postState.content.errors.push('composer.errors.google.gifs')
      postState.hasErrors = true
    }

    return postState
  }
)

export const composerYoutubePostStatusSelector = createSelector(
  youtubePostSelector,
  composerMediaIsEmptySelector,
  composerVideoUrlSelector,
  composerSelectedProfilesSelector,
  composerDocumentUploadSelector,
  (post: ComposerYoutubePost, mediaIsEmpty, url, profiles, document) => {
    const postState = emptyPostStatus()
    const ytProfiles = profiles.filter(p => p.type === BRAND_YOUTUBE)

    if (ytProfiles.length === 0) {
      return postState
    }

    if (document) {
      postState.content.errors.push('composer.errors.youtube.document-post')
      postState.hasErrors = true
    }

    const status = post.title.trim()

    if (status.length === 0) {
      if (mediaIsEmpty) {
        postState.isEmpty = true
        postState.content.errors.push('composer.errors.youtube.post-empty')
        postState.hasErrors = true
      }
    } else if (status.length > MAX_YT_TITLE_LENGTH) {
      postState.content.errors.push('composer.errors.youtube.post-length')
      postState.hasErrors = true
    }

    if (mediaIsEmpty && post.title.trim().length === 0) {
      postState.isEmpty = true
    }

    if (!url) {
      postState.content.errors.push('composer.errors.youtube.no-video')
      postState.hasErrors = true
    }

    if (!postState.isEmpty) {
      postState.content.info.push('composer.info.youtube.shorts')
    }
    return postState
  }
)

export const composerThreadsPostStatusSelector = createSelector(
  threadsPostSelector,
  composerImageUrlsSelector,
  composerSelectedProfilesSelector,
  composerMediaIsEmptySelector,
  composerDocumentUploadSelector,
  (post, images, profiles, mediaIsEmpty, document) => {
    const postState = emptyPostStatus()
    const threadsProfiles = profiles.filter((p: PostDestination) => p.type === BRAND_THREADS)

    if (threadsProfiles.length === 0) {
      return postState
    }

    const status = post.status.text.trim()
    if (status.length > MAX_THREADS_STATUS_LENGTH) {
      postState.content.errors.push('composer.errors.threads.status-length')
      postState.hasErrors = true
    }

    if (document) {
      postState.content.errors.push('composer.errors.threads.document-post')
      postState.hasErrors = true
    }

    if (mediaIsEmpty && !post.status.text.trim()) {
      postState.isEmpty = true
      postState.content.errors.push('composer.errors.threads.post-empty')
      postState.hasErrors = true
    }

    if (images.length > MAX_THREADS_IMAGES) {
      postState.content.errors.push('composer.errors.threads.max-photos')
      postState.hasErrors = true
    }
    return postState
  }
)

export const composerPostStatusByNetworkSelector = createSelector(
  composerInstagramPostStatusSelector,
  composerPinterestPostStatusSelector,
  composerLinkedinPostStatusSelector,
  composerFacebookPostStatusSelector,
  composerTwitterPostStatusSelector,
  composerTiktokPostStatusSelector,
  composerGooglePostStatusSelector,
  composerYoutubePostStatusSelector,
  composerThreadsPostStatusSelector,
  composerBlueskyPostStatusSelector,
  (
    igPost: ComposerPostStatus,
    piPost: ComposerPostStatus,
    liPost: ComposerPostStatus,
    fbPost: ComposerPostStatus,
    twPost: ComposerPostStatus,
    tkPost: ComposerPostStatus,
    gPost: ComposerPostStatus,
    ytPost: ComposerPostStatus,
    thPost: ComposerPostStatus,
    bsPost: ComposerPostStatus
  ) => {
    return {
      [BRAND_FACEBOOK]: fbPost,
      [BRAND_TWITTER]: twPost,
      [BRAND_LINKEDIN]: liPost,
      [BRAND_PINTEREST]: piPost,
      [BRAND_INSTAGRAM]: igPost,
      [BRAND_TIKTOK]: tkPost,
      [BRAND_GOOGLE]: gPost,
      [BRAND_YOUTUBE]: ytPost,
      [BRAND_THREADS]: thPost,
      [BRAND_BLUESKY]: bsPost
    }
  }
)

export const composerIsEmptySelector = createSelector(
  composerPostStatusByNetworkSelector,
  composerTabsSelector,
  (statusByNetwork, tabs) => {
    for (const tab of tabs) {
      if (!(statusByNetwork as Record<string, ComposerPostStatus>)[tab].isEmpty) {
        return false
      }
    }
    return true
  })

export interface ComposerReadinessState {
  hasErrors: boolean
  isOptimized: boolean
  emptyNetworks: string[]
  isUploading: boolean
}

export const composerReadinessSelector = createSelector(
  composerPostStatusByNetworkSelector,
  composerIsUploadingSelector,
  (statusByNetwork, isUploading): ComposerReadinessState => {
    const statuses = Object.values(statusByNetwork) as ComposerPostStatus[]
    const networkRank = (network: string) => {
      const index = NETWORKS_ORDERED.indexOf(network)
      return index === -1 ? NETWORKS_ORDERED.length : index
    }
    return {
      hasErrors: statuses.some(status => status.hasErrors || status.isEmpty),
      isOptimized: !statuses.some(status => status.hasHints),
      // Derived from the same status map as hasErrors so a new network can never
      // flag an error without also being nameable; sorted into canonical order.
      emptyNetworks: Object.keys(statusByNetwork)
        .filter(network => (statusByNetwork as Record<string, ComposerPostStatus>)[network].isEmpty)
        .sort((a, b) => networkRank(a) - networkRank(b)),
      // NB: composerIsUploadingSelector returns 'video' | boolean
      isUploading: Boolean(isUploading)
    }
  }
)

export const composerResetKeySelector = createSelector(composerSelector, state => state.resetKey)
export const savedStatusesSelector = createSelector(composerSelector, state => state.savedStatuses)
export const PINTEREST_BOARD_NOT_SELECTED_MESSAGE_KEY = 'composer.errors.pinterest.no-board'
export const facebookBackgroundTemplatesSelector = createSelector(composerSelector, state => state.facebookBackgroundTemplates)
export const defaultFacebookBackgroundTemplateIdSelector = createSelector(composerSelector, state => {
  return state.defaultFacebookBackgroundTemplateId
})
